import { api } from '@api'
import type { RecordingForm } from './recordingSchema'

export const fetchEvents = async (search: string) => {
  let query = api()
    .from('events')
    .select('id, group')
    .order('starts_at', { ascending: false })
    .limit(20)

  // Apply search filter if a string exists
  if (search.trim()) {
    query = query.ilike('description', `%${search}%`)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching events:', error)
    throw new Error(error.message)
  }

  // Map to the format expected by Ark UI / Combobox
  return (data || []).map((event) => ({
    label: event.group || '',
    value: event.id,
  }))
}

export const fetchGroups = async (search: string) => {
  let query = api()
    .from('recordings')
    .select('group')
    .not('group', 'is', null)
    .order('group', { ascending: true })

  if (search.trim()) {
    query = query.ilike('group', `%${search}%`)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching groups:', error)
    throw new Error(error.message)
  }

  // Deduplicate and map to { label, value }
  const uniqueGroups = Array.from(new Set((data || []).map((r) => r.group)))

  return uniqueGroups.map((group) => ({
    label: group as string,
    value: group as string,
  }))
}

export const saveRecording = async (values: RecordingForm) => {
  const { data, error } = await api()
    .from('recordings')
    .insert(values)
    .select()
    .single()

  if (error) {
    // Supabase errors provide detailed messages
    throw new Error(error.message)
  }

  return data
}

export const updateRecording = async ({
  id,
  values,
}: { id: string; values: RecordingForm }) => {
  const { data, error } = await api()
    .from('recordings')
    .update(values)
    .eq('id', id)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data
}

export const fetchProfiles = async (search: string) => {
  let query = api()
    .from('profile')
    .select('id, username')
    .order('username', { ascending: true })
    .limit(20)

  // Filter by username if the user is typing
  if (search.trim()) {
    query = query.ilike('username', `%${search}%`)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching profiles:', error)
    throw new Error(error.message)
  }

  // Map to DataItem format
  return (data || []).map((profile) => ({
    label: profile.username || 'Unknown User',
    value: profile.id,
  }))
}
